/**
 * Client Permit entity DTOs — permits ISSUED TO the account by their client
 * (the hospital / school / factory whose site they work on), as opposed to
 * `PermitToWork` (`./permit.ts`), which the account issues on a site it runs.
 *
 * Mirrors Prisma `ClientPermit`. The client keeps the original paper, so the
 * captured photos/PDFs ARE the record: one set taken when the permit is handed
 * over (`receivedFileUrls`), a second set of the completed/signed form taken at
 * sign-off just before it goes back (`signedOffFileUrls`).
 *
 * THE SINGLE SOURCE for the type list, its display labels, the attachment
 * helpers and the write schemas. `lib/client-permits.ts` re-exports every
 * symbol below so the web page, the web API routes, the mobile API routes, the
 * audit pack and the native screens all read one definition — a label or rule
 * cannot drift between the page and the printed evidence, or between web and
 * mobile. (Web-only extras, such as the field-correction update schema, stay in
 * `lib/client-permits.ts`.)
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

export const CLIENT_PERMIT_TYPES = [
  "HOT_WORKS",
  "CONFINED_SPACE",
  "LIVE_ELECTRICAL",
  "ELECTRICAL_ISOLATION",
  "WORKING_AT_HEIGHT",
  "EXCAVATION",
  "FIRE_ALARM_ISOLATION",
  "MEDICAL_GAS",
  "OTHER",
] as const;

export type ClientPermitTypeValue = (typeof CLIENT_PERMIT_TYPES)[number];

export const ClientPermitTypeSchema = z.enum(CLIENT_PERMIT_TYPES);

export const CLIENT_PERMIT_TYPE_LABELS: Record<ClientPermitTypeValue, string> = {
  HOT_WORKS: "Hot Works",
  CONFINED_SPACE: "Confined Space",
  LIVE_ELECTRICAL: "Live Electrical Systems",
  ELECTRICAL_ISOLATION: "Electrical Isolation",
  WORKING_AT_HEIGHT: "Working at Height",
  EXCAVATION: "Excavation",
  FIRE_ALARM_ISOLATION: "Fire Alarm Isolation",
  MEDICAL_GAS: "Medical Gas",
  OTHER: "Other",
};

/**
 * Display label for a permit — "Other" permits show the free-text label the
 * supervisor typed (the permit's own header), falling back to "Other".
 */
export function clientPermitTypeLabel(
  permitType: string,
  otherTypeLabel?: string | null,
): string {
  if (permitType === "OTHER" && otherTypeLabel && otherTypeLabel.trim()) {
    return otherTypeLabel.trim();
  }
  return (
    CLIENT_PERMIT_TYPE_LABELS[permitType as ClientPermitTypeValue] ??
    permitType.replace(/_/g, " ")
  );
}

/** True when a stored attachment URL points at a PDF rather than a photo. */
export function isPdfAttachmentUrl(url: string): boolean {
  try {
    // Query strings (presigned URLs) must not defeat the extension check.
    const pathname = url.includes("://") ? new URL(url).pathname : url;
    return pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return url.toLowerCase().split("?")[0]!.endsWith(".pdf");
  }
}

/**
 * Attachment-list cap. Mirrors the server's `MAX_PHOTO_URLS`
 * (`lib/storage/photo-limits.ts`) — the contracts package may not import from
 * the Next.js app, so the two are pinned equal by
 * `__tests__/lib/client-permits.test.ts`.
 */
export const CLIENT_PERMIT_MAX_FILE_URLS = 20;

/**
 * The `client_permit_file` upload policy, mirrored from the server signer
 * (`lib/storage/presign.ts` POLICIES) so a client can refuse an over-limit
 * capture BEFORE it is queued for an offline replay. A queued capture that the
 * server will permanently 4xx is worse than an immediate error: the sync UI can
 * only retry the same invalid payload, and the permit has gone back to the
 * client by then. Pinned equal to the signer's policy by
 * `__tests__/lib/storage/presign.test.ts`.
 */
export const CLIENT_PERMIT_FILE_MAX_BYTES = 20 * 1024 * 1024;
export const CLIENT_PERMIT_FILE_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
  "application/pdf",
] as const;
/** Longest filename the presign request schema accepts. */
export const CLIENT_PERMIT_FILE_MAX_FILENAME = 255;

export type ClientPermitFileContentType =
  (typeof CLIENT_PERMIT_FILE_CONTENT_TYPES)[number];

export const ClientPermitFileContentTypeSchema = z.enum(
  CLIENT_PERMIT_FILE_CONTENT_TYPES,
);

/**
 * One capture as the client holds it before transfer. Validating this at
 * CAPTURE time is what turns "queued, then permanently rejected hours later"
 * into "fix it while the permit is still in your hand".
 */
export const ClientPermitCaptureFileSchema = z
  .object({
    uri: z.string().min(1),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(CLIENT_PERMIT_FILE_MAX_BYTES),
    contentType: ClientPermitFileContentTypeSchema,
    filename: z.string().min(1).max(CLIENT_PERMIT_FILE_MAX_FILENAME),
  })
  .strict();

export type ClientPermitCaptureFileInput = z.infer<
  typeof ClientPermitCaptureFileSchema
>;

const fields = {
  permitType: ClientPermitTypeSchema,
  otherTypeLabel: z.string().trim().max(120).nullable(),
  issuedByName: z.string().trim().min(1).max(160),
  fileUrls: z
    .array(z.string().min(1).max(2048))
    .max(CLIENT_PERMIT_MAX_FILE_URLS),
};

/**
 * READ shape. `userId` / `signedOffByUserId` are recorder provenance, not
 * ownership — both are `SetNull` in Prisma so the evidence outlives the
 * account of whoever captured it.
 */
export const ClientPermitSchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    userId: z.string().nullable(),
    permitType: ClientPermitTypeSchema,
    otherTypeLabel: z.string().nullable(),
    issuedByName: z.string(),
    receivedFileUrls: z.array(z.string()),
    signedOffFileUrls: z.array(z.string()),
    signedOffAt: z.string().datetime().nullable(),
    signedOffByUserId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type ClientPermit = z.infer<typeof ClientPermitSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const ClientPermitStrict = ClientPermitSchema.strict();

/**
 * CREATE — the photo IS the record (the client keeps the original paper), so
 * at least one received attachment is required. `otherTypeLabel` is required
 * exactly when the type is OTHER: a bare "Other" row is indistinguishable in
 * the list and worthless in the audit pack three years later.
 */
export const ClientPermitCreateRequest = z
  .object({
    permitType: fields.permitType,
    otherTypeLabel: fields.otherTypeLabel.optional(),
    issuedByName: fields.issuedByName,
    receivedFileUrls: fields.fileUrls.refine((urls) => urls.length > 0, {
      message: "At least one photo or PDF of the permit is required",
    }),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.permitType === "OTHER" && !body.otherTypeLabel?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherTypeLabel"],
        message: "Describe the permit type when choosing Other",
      });
    }
  });

export type ClientPermitCreateRequest = z.infer<
  typeof ClientPermitCreateRequest
>;

/**
 * SIGN-OFF — the moment before the signed original is handed back to the
 * client. The photo of the completed/signed form is the evidence the permit's
 * conditions (fire watch included — it is signed AFTER the safety period) were
 * discharged, so it is required.
 */
export const ClientPermitSignOffRequest = z
  .object({
    action: z.literal("SIGN_OFF"),
    signedOffFileUrls: fields.fileUrls.refine((urls) => urls.length > 0, {
      message: "Add a photo of the signed-off permit before handing it back",
    }),
  })
  .strict();

export type ClientPermitSignOffRequest = z.infer<
  typeof ClientPermitSignOffRequest
>;

/** The shared field builders, for `lib/client-permits.ts`'s web-only update schema. */
export const clientPermitFields = fields;
